import { Component, OnInit, Input, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';
import { RewardFormSteps } from 'src/app/shared/models/reward-form/reward-form';
import { RewardInfoData } from '../reward-info/reward-info.component';
import { ProductDTO } from 'src/app/shared/models/dtos/responses/product-dto/product-dto';

@Component({
  selector: 'app-reward-overview-form',
  templateUrl: './reward-overview-form.component.html',
  styleUrls: ['./reward-overview-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Component for displaying reward overview form
 */
export class RewardOverviewFormComponent implements OnInit {
  readonly DATE_PLACEHOLDER = 'dd/mm/yyyy';

  @Input() formGroup!: FormGroup<RewardFormSteps>;
  @Input() rewardId?: number;

  @Input() availableFamilies!: any[];

  @Input() availableBusinessLevels!: any;

  startDateControl!: FormControl;
  endDateControl!: FormControl;

  isEndDateDisabled = false;
  labels = {
    startDate: 'start-date',
    endDate: 'end-date',
    setRewardPeriod: 'set-reward-period',
    undeterminedEndDate: 'undetermined-end-date',
  };
  minDate = new Date();

  reward!: RewardResponseDTO;
  rewardInfoData!: RewardInfoData;

  /**
   * Constructor for RewardOverviewFormComponent
   */
  constructor(private readonly translationService: TranslationService) {}

  /**
   * Lifecycle hook that is called after data-bound properties are initialized
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);

    const formGroupControls = this.formGroup.controls;

    this.reward = this.getRewardFromStep1(formGroupControls);
    this.rewardInfoData = this.getRewardInfoDataFromSteps(formGroupControls);

    this.startDateControl = formGroupControls.step3.controls.startDate;
    this.endDateControl = formGroupControls.step3.controls.endDate;

    if (!this.rewardId || (this.reward && this.endDateControl.value)) {
      this.endDateControl.setValidators(Validators.required);
      this.isEndDateDisabled = false;
    } else if (this.reward && this.rewardId && !this.endDateControl.value) {
      this.isEndDateDisabled = true;
      this.endDateControl.clearValidators();
    }
    this.endDateControl.updateValueAndValidity();

    if (this.minDate !== this.startDateControl.value) {
      this.minDate = this.startDateControl.value;
    }

    this.startDateControl.valueChanges.subscribe((newStart: Date | null) => {
      if (
        newStart &&
        this.endDateControl.value &&
        this.endDateControl.value < newStart
      ) {
        this.endDateControl.setValue(null);
      }
    });
  }

  /**
   * Handles the change event from the "undetermined end date" checkbox.
   * If the checkbox is checked, the end date control is cleared.
   * @param {MatCheckboxChange} event
   */
  onEndDateDisableChange(event: MatCheckboxChange) {
    if (event.checked) {
      this.endDateControl.setValue(null);
      this.endDateControl.removeValidators(Validators.required);
    } else {
      this.endDateControl.setValidators(Validators.required);
    }
    this.endDateControl.updateValueAndValidity();
  }

  /**
   * Converts step1 data into RewardResponseDto.
   * @param {RewardFormSteps} steps
   * @return {RewardResponseDTO}
   */
  private getRewardFromStep1(steps: RewardFormSteps) {
    const controls = steps.step1.controls;

    const name = controls.relatedProduct.value?.label ?? controls.name.value!;

    return new RewardResponseDTO(
      controls.rewardCost.value!,
      controls.productImage.value!,
      controls.rewardDiscount.value!,
      controls.rewardType.value!.value!,
      [],
      [],
      [],
      new Date(),
      new Date(),
      controls.redemptionLimit.value!,
      controls.extensionPeriod.value!,
      true,
      controls.isRelatedToProduct.value!,
      controls.rewardCode.value!,
      0,
      name,
      true,
      [],
      {} as ProductDTO
    );
  }

  /**
   * Converts step1 and step2 data into RewardInfoData.
   * @param {RewardFormSteps} steps
   * @return {RewardInfoData}
   */
  private getRewardInfoDataFromSteps(steps: RewardFormSteps): RewardInfoData {
    const step1Controls = steps.step1.controls;
    const step2Controls = steps.step2.controls;

    return {
      rewardType: step1Controls.rewardType.value,
      relatedProductName: step1Controls.relatedProduct.value?.label ?? null,
      name: step1Controls.name.value,
      namePt: step1Controls.namePt.value,
      nameEs: step1Controls.nameEs.value,
      redemptionLimit: step1Controls.redemptionLimit.value,
      rewardCost: step1Controls.rewardCost.value,
      rewardCode: step1Controls.rewardCode.value,
      extensionPeriod: step1Controls.extensionPeriod.value,
      vipLevels: step1Controls.vipLevels.value,
      vipOnly: step1Controls.vipLevels.value?.length! > 0,
      families: step1Controls.rewardFamilies.value!,
      locationRestrictions: step2Controls.regionExceptions.value,
    };
  }
}
